import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { InspectorPanel } from 'components/wysiwyg/InspectorPanel';
import { TemplatesSidebar } from 'components/wysiwyg/TemplatesSidebar';
import { EditorToolbar } from 'components/wysiwyg/EditorToolbar';
import { PreviewArea } from 'components/wysiwyg/PreviewArea';
import { DataScenario, RenderRequest, Variant, VariantWithTemplate, PreviewHoverPayload, PreviewExportHandle, ExportAction, SUPPORTED_EXPORT_CHANNELS, ExportInProgressAction, TextField } from 'types/apis/cxEditor';
import { createCxApiClient } from 'framework/http/CxEditorClient';
import { useGlobalConfig } from 'context/GlobalContext';
import { WidgetSdkProvider } from 'context/WidgetSdkContext';
import { WidgetSdkErrorBoundary } from 'context/WidgetSdkErrorBoundary';
import { WidgetErrorBoundary } from 'components/wysiwyg/WidgetErrorBoundary';
import CompareDialog from 'components/wysiwyg/CompareDialog';
import { toast } from 'utils/toast';
import { Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { resolveScenarioId } from 'utils/cx-editor';
import { getFuelTypeFromTemplateKey } from 'apis/dataScenariosAPI';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedPilotId, selectPilotList } from 'store/dataReducers/pilotsSlice';
import { selectProjectAttributes } from 'store/dataReducers/applicationScopeSlice';
import {
  selectLevel1Data,
  selectLevel2Data,
  selectLastFetchedPilotId,
  selectFeatureMetricsLoading,
  setLevel1Data,
  setLevel2Data,
  setLoading,
  setError,
  setLastFetchedPilotId,
  clearFeatureMetrics
} from 'store/dataReducers/featureMetricsSlice';
import {
  setDataScenarios,
  setDataScenariosLoading,
  setDataScenariosError,
  selectDataScenariosByKey,
} from 'store/dataReducers/dataScenariosSlice';

const DEFAULT_VARIANT: Variant = { hierarchy: 'Project', locale: 'en_US', scenario: 'default' };

// Module-level in-flight tracking to prevent duplicate API calls across component instances and StrictMode re-renders
const inFlightScenarioRequests = new Set<string>(); // scenarios-${templateKey}-${pilotId}-${fuelType}-${locale}
const inFlightTemplateMetaRequests = new Set<string>(); // ${templateKey}-${elementKey}

function buildScenarioOptions(
  dataScenarios: DataScenario[],
  templateKey: string,
  channel: string
): Array<{ label: string; value: string }> {
  const baseTemplateKey = templateKey.split('__')[0];
  const options = dataScenarios
    .filter((ds: DataScenario) => {
      const isWeb = channel?.toLowerCase() === 'web';
      let matchesTemplate = ds.templateName === baseTemplateKey;
      if (!matchesTemplate && isWeb && baseTemplateKey.startsWith('DASHBOARD.')) {
        matchesTemplate = ds.templateName === 'DASHBOARD';
      }
      return matchesTemplate && (ds.productType || '').toLowerCase() === (channel || 'Email').toLowerCase();
    })
    .map((ds: DataScenario) => ({ label: ds.scenarioDisplayName || ds.scenarioId, value: ds.scenarioId }));

  options.sort((a, b) => {
    const leftDefault = dataScenarios.find((ds: DataScenario) => ds.scenarioId === a.value)?.defaultScenario;
    const rightDefault = dataScenarios.find((ds: DataScenario) => ds.scenarioId === b.value)?.defaultScenario;
    if (leftDefault && !rightDefault) return -1;
    if (!leftDefault && rightDefault) return 1;
    return a.label.localeCompare(b.label);
  });

  return options.length > 0 ? options : [{ label: 'Default', value: 'default' }];
}

export const WysiwygPage: React.FC = () => {
  const { rc } = useGlobalConfig();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const pilotList = useSelector(selectPilotList);
  const projectAttributes = useSelector(selectProjectAttributes);
  const lastFetchedPilotId = useSelector(selectLastFetchedPilotId);
  const featureMetricsLoading = useSelector(selectFeatureMetricsLoading);
  const level1Data = useSelector(selectLevel1Data);

  // Read URL params for state restoration (when navigating from EditColorPalettePage)
  const templateKeyParam = searchParams.get('templateKey');
  const elementKeyParam = searchParams.get('elementKey');
  const localeParam = searchParams.get('locale');
  const scenarioParam = searchParams.get('scenario');

  // Get environment and pilotId from Redux state
  const selectedPilot = React.useMemo(() => {
    if (!selectedPilotId || !pilotList.length) return null;
    return pilotList.find((pilot: any) => String(pilot.projectId) === String(selectedPilotId));
  }, [selectedPilotId, pilotList]);

  // SDK loading is handled by WidgetSdkProvider wrapper
  // No need to load SDK here to avoid duplicate loading attempts
  // Store templateKey and selectedFuelType as separate state
  const [templateKey, setTemplateKey] = React.useState<string>(templateKeyParam || '');
  const [selectedFuelType, setSelectedFuelType] = React.useState<string | undefined>(undefined);
  const [elementKey, setElementKey] = React.useState<string>(elementKeyParam || 'Full Template');
  const [variant, setVariant] = React.useState<Variant>({
    ...DEFAULT_VARIANT,
    locale: localeParam || DEFAULT_VARIANT.locale,
    scenario: scenarioParam || DEFAULT_VARIANT.scenario,
  });
  const [values, setValues] = React.useState<Record<string, any>>({});
  const [renderedHtml, setRenderedHtml] = React.useState<string>(''); // Store rendered HTML for technical section ID lookup
  const [compareOpen, setCompareOpen] = React.useState(false);
  const [variantB, setVariantB] = React.useState<VariantWithTemplate | null>(null);
  const [availableScenarios, setAvailableScenarios] = React.useState<Array<{ value: string; label: string }>>([]);
  const [scenariosLoading, setScenariosLoading] = React.useState(false);
  const [availableElements, setAvailableElements] = React.useState<Array<{ elementKey: string; displayName: string }>>([
    { elementKey: 'Full Template', displayName: 'Full Template' }
  ]); // Elements parsed from rendered HTML
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [inspectorOpen, setInspectorOpen] = React.useState(true);
  const [previewMode, setPreviewMode] = React.useState<'desktop' | 'mobile'>('desktop');
  const inspectorSaveRef = React.useRef<(() => Promise<void>) | null>(null); // Store InspectorPanel save function
  const inspectorCancelRef = React.useRef<(() => void) | null>(null); // Store InspectorPanel cancel function
  const previewAreaRef = React.useRef<PreviewExportHandle>(null);
  const [modifiedCount, setModifiedCount] = React.useState<number>(0); // Track number of modified fields
  const [refreshKey, setRefreshKey] = React.useState<number>(0); // Refresh trigger to force preview re-render after save
  const [previewBusy, setPreviewBusy] = React.useState(false); // Disable template/section/scenario changes while preview is rendering
  const [exportInProgressAction, setExportInProgressAction] = React.useState<ExportInProgressAction>(null);
  const [hoverPayload, setHoverPayload] = React.useState<PreviewHoverPayload | null>(null); // Preview hover state for inspector matching
  const [isHoverPinned, setIsHoverPinned] = React.useState(false);
  const [inspectorActiveTab, setInspectorActiveTab] = React.useState<'text' | 'colors' | 'images' | 'metadata'>('text');
  const [hoverInvestigateEnabled, setHoverInvestigateEnabled] = React.useState(false);
  const [smsTextFields, setSmsTextFields] = React.useState<TextField[]>([]);
  const didInitTemplateRef = React.useRef(false);
  
  // Clear URL params after state restoration (to keep URL clean)
  React.useEffect(() => {
    if (templateKeyParam || elementKeyParam || localeParam || scenarioParam) {
      setSearchParams({}, { replace: true });
    }
  }, []);

  // Reset local component state when the user switches to a different pilot.
  // Without this, stale templateKey / elementKey / variant from the previous
  // pilot would persist and cause blank widgets or incorrect renders.
  const prevPilotIdRef = React.useRef(selectedPilotId);
  React.useEffect(() => {
    if (prevPilotIdRef.current && selectedPilotId && prevPilotIdRef.current !== selectedPilotId) {
      setTemplateKey('');
      setSelectedFuelType(undefined);
      setElementKey('Full Template');
      setVariant({ ...DEFAULT_VARIANT });
      setValues({});
      setRenderedHtml('');
      setAvailableScenarios([]);
      setAvailableElements([{ elementKey: 'Full Template', displayName: 'Full Template' }]);
      setShouldFetchTemplateMeta(false);
      lastFetchedTemplateKeyRef.current = '';
      didInitTemplateRef.current = false;
    }
    prevPilotIdRef.current = selectedPilotId;
  }, [selectedPilotId]); 

  // Confirmation dialog state for unsaved changes
  const [unsavedChangesDialog, setUnsavedChangesDialog] = React.useState<{
    open: boolean;
    pendingAction: 'template' | 'element' | 'scenario';
    pendingValue: string | Variant | { templateKey: string; fuelType?: string };
  }>({
    open: false,
    pendingAction: 'template',
    pendingValue: '',
  });

  // Fetch Level 1 and Level 2 feature metrics on mount or when pilotId changes
  React.useEffect(() => {
    if (!selectedPilotId) {
      return;
    }

    // Skip if we already have data for this pilot
    if (lastFetchedPilotId === String(selectedPilotId)) {
      return;
    }

    // Clear stale data immediately so the sidebar doesn't flash old items
    dispatch(clearFeatureMetrics());

    let ignore = false;

    async function fetchFeatureMetrics() {
      dispatch(setLoading(true));
      dispatch(setError(null));

      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        const pilotIdStr = String(selectedPilotId);

        console.log('[WysiwygPage] Fetching Level 1 and Level 2 feature metrics for pilot:', pilotIdStr);

        // Fetch both APIs in parallel
        const [level1Response, level2Response] = await Promise.all([
          api.getFeatureMetrics(pilotIdStr, 'LEVEL_1'),
          api.getFeatureMetrics(pilotIdStr, 'LEVEL_2')
        ]);

        if (!ignore) {
          dispatch(setLevel1Data(level1Response));
          dispatch(setLevel2Data(level2Response));
          dispatch(setLastFetchedPilotId(pilotIdStr));
          dispatch(setLoading(false));
          console.log('[WysiwygPage] Feature metrics loaded successfully', {
            level1Count: level1Response.length,
            level2Count: level2Response.length
          });
        }
      } catch (err: any) {
        if (!ignore) {
          console.error('[WysiwygPage] Failed to fetch feature metrics:', err);
          dispatch(setError(err.message || 'Failed to load feature metrics'));
          dispatch(setLoading(false));
        }
      }
    }

    fetchFeatureMetrics();

    return () => {
      ignore = true;
    };
  }, [selectedPilotId, rc, dispatch, lastFetchedPilotId]);

  // Extract fuel_type from templateKey first, then fall back to projectAttributes
  const fuelType = React.useMemo(() => {
    // First, try to extract fuel type from template key
    const templateFuelType = getFuelTypeFromTemplateKey(templateKey || '');
    if (templateFuelType) {
      console.log('[WysiwygPage] Extracted fuel type from template key:', {
        templateKey,
        fuelType: templateFuelType
      });
      return templateFuelType;
    }

    // Fall back to projectAttributes if no fuel type in template key
    let fuelType = 'ELECTRIC'; // Default fallback
    if (projectAttributes?.fuel_type) {
      // Handle different formats: string, array, or comma-separated
      if (typeof projectAttributes.fuel_type === 'string') {
        // If comma-separated, take the first one
        const fuelTypes = projectAttributes.fuel_type.split(',').map(f => f.trim());
        fuelType = fuelTypes[0]?.toUpperCase() || 'ELECTRIC';
      } else if (Array.isArray(projectAttributes.fuel_type)) {
        const fuelTypeArray = projectAttributes.fuel_type as any[];
        if (fuelTypeArray.length > 0) {
          fuelType = String(fuelTypeArray[0]).toUpperCase();
        }
      }
    }
    console.log('[WysiwygPage] Using fuel type from project attributes:', { fuelType });
    return fuelType;
  }, [templateKey, projectAttributes]);

  // Initialize variantA when templateKey or variant changes
  // Use useMemo to prevent unnecessary object recreation
  const variantA = React.useMemo(() => {
    if (!templateKey) return null;
    return {
      templateKey, // Clean template key (no fuel suffix)
      fuelType: selectedFuelType, // Separate fuel type property
      ...variant,
    };
  }, [templateKey, selectedFuelType, variant.hierarchy, variant.locale, variant.scenario, variant.clusterId]);

  // Track if we should fetch template meta after render completes (for optimization)
  const [shouldFetchTemplateMeta, setShouldFetchTemplateMeta] = React.useState(false);
  const lastFetchedTemplateKeyRef = React.useRef<string>('');
  
  // Handle render completion - trigger template/scenario fetch (optimization, not required)
  const handleRenderComplete = React.useCallback((completedTemplateKey: string) => {
    // Only trigger if this is a new template (not already fetched)
    if (completedTemplateKey && completedTemplateKey !== lastFetchedTemplateKeyRef.current) {
      setShouldFetchTemplateMeta(true);
    }
  }, []);
  
  // Determine channel from templateKey for PreviewArea
  const channel = React.useMemo(() => {
    if (!templateKey) return 'Email';
    if (templateKey.startsWith('WEB_') || templateKey.startsWith('DASHBOARD.')) return 'Web';
    if (templateKey.startsWith('HER.') || templateKey === 'HER') return 'HER';
    if (templateKey.startsWith('SMS.')) return 'SMS';
    return 'Email';
  }, [templateKey]);

  const effectiveFuelType = React.useMemo((): string | null | undefined => {
    if (channel === 'Web') return selectedFuelType ?? fuelType;
    const fromKey = getFuelTypeFromTemplateKey(templateKey) ?? null;
    if (channel === 'HER' && templateKey.includes('DFHER') && fromKey == null) return 'ELECTRIC';
    return fromKey;
  }, [channel, selectedFuelType, fuelType, templateKey]);

  const cachedScenariosForKey = useSelector((state: any) =>
    selectDataScenariosByKey(state, String(selectedPilotId ?? ''), effectiveFuelType ?? '', variant.locale || 'en_US')
  );

  const canExport =
    elementKey === 'Full Template' &&
    !previewBusy &&
    !exportInProgressAction &&
    !compareOpen &&
    (SUPPORTED_EXPORT_CHANNELS as ReadonlyArray<string>).includes(channel);

  const exportDisabledReason = !templateKey
    ? 'Select a template first'
    : !(SUPPORTED_EXPORT_CHANNELS as ReadonlyArray<string>).includes(channel)
    ? 'Export not supported for this channel'
    : compareOpen
    ? 'Export not available in compare mode'
    : previewBusy
    ? 'Preview is loading'
    : exportInProgressAction
    ? exportInProgressAction === 'clipboard'
      ? 'Copy in progress'
      : 'Download in progress'
    : elementKey !== 'Full Template'
    ? 'Select Full Template to export'
    : undefined;

  const handleExport = React.useCallback(async (action: ExportAction) => {
    if (!previewAreaRef.current || exportInProgressAction) return;

    setExportInProgressAction(action);
    toast.info(action === 'download' ? 'Preparing PNG download...' : 'Copying image to clipboard...');

    try {
      const result = await previewAreaRef.current.exportPreview(action);
      if (result.ok) {
        toast.success(action === 'download' ? 'Preview downloaded' : 'Copied to clipboard');
      } else {
        const messages: Record<string, string> = {
          'capture-failed': 'Failed to capture preview — try again',
          'clipboard-denied': 'Clipboard access denied — allow it in browser settings',
          'not-ready': 'Preview is not ready yet',
          'unsupported': 'Export not supported for this preview mode',
        };
        toast.error(messages[result.reason] ?? 'Export failed');
      }
    } finally {
      setExportInProgressAction(null);
    }
  }, [exportInProgressAction]);

  // When switching templates (product type/email type), always reset section to "Full Template"
  // so the new selection starts from a predictable default.
  // Skip the first run to avoid clobbering URL-param state restoration.
  React.useEffect(() => {
    if (!didInitTemplateRef.current) {
      didInitTemplateRef.current = true;
      return;
    }
    setElementKey('Full Template');
    setAvailableElements([{ elementKey: 'Full Template', displayName: 'Full Template' }]);
    setRenderedHtml('');
    setVariant(prev => ({ ...prev, scenario: 'default' }));
    setHoverPayload(null); // Clear hover on template change
  }, [templateKey]);

  // Fetch scenarios when templateKey/pilotId/effectiveFuelType/locale/channel changes.
  // Checks Redux cache (targeted selector) first. Retries 3 times on transient 5xx errors.
  React.useEffect(() => {
    if (!templateKey || !selectedPilotId) return;

    const locale = variant.locale || 'en_US';
    const pilotId = String(selectedPilotId);
    const requestKey = `scenarios-${templateKey}-${pilotId}-${effectiveFuelType ?? ''}-${locale}`;

    if (inFlightScenarioRequests.has(requestKey)) {
      console.log('[WysiwygPage] Scenarios request already in flight, skipping duplicate:', requestKey);
      return;
    }

    if (cachedScenariosForKey && cachedScenariosForKey.length > 0) {
      setAvailableScenarios(buildScenarioOptions(cachedScenariosForKey, templateKey, channel));
      return;
    }

    inFlightScenarioRequests.add(requestKey);
    let ignore = false;

    async function fetchWithRetry(attemptsLeft: number): Promise<any> {
      const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
      try {
        return await api.getDataScenarios(pilotId, effectiveFuelType, locale);
      } catch (err: any) {
        const status = err?.response?.status ?? err?.status;
        if (attemptsLeft > 1 && status >= 500) {
          const delay = (3 - attemptsLeft) === 0 ? 1000 : 2000;
          await new Promise(res => setTimeout(res, delay));
          return fetchWithRetry(attemptsLeft - 1);
        }
        throw err;
      }
    }

    async function loadScenarios() {
      dispatch(setDataScenariosLoading({ pilotId, fuelType: effectiveFuelType ?? '', locale, loading: true }));
      setScenariosLoading(true);

      try {
        const dataScenariosResp = await fetchWithRetry(3);
        if (ignore) return;
        inFlightScenarioRequests.delete(requestKey);

        const dataScenarios = dataScenariosResp?.dataScenarios as DataScenario[] | undefined;
        if (dataScenarios && dataScenarios.length > 0) {
          dispatch(setDataScenarios({ pilotId, fuelType: effectiveFuelType ?? '', locale, dataScenarios }));
        }
        setAvailableScenarios(buildScenarioOptions(dataScenarios ?? [], templateKey, channel));
      } catch (error: any) {
        if (!ignore) {
          console.error('[WysiwygPage] Failed to load data scenarios:', error);
          inFlightScenarioRequests.delete(requestKey);
          dispatch(setDataScenariosError({
            pilotId,
            fuelType: effectiveFuelType ?? '',
            locale,
            error: error?.message || 'Failed to load data scenarios',
          }));
          setAvailableScenarios(prev => {
            if (prev.length === 0) return [{ label: 'Default', value: 'default' }];
            return prev;
          });
        }
      } finally {
        if (!ignore) {
          dispatch(setDataScenariosLoading({ pilotId, fuelType: effectiveFuelType ?? '', locale, loading: false }));
          setScenariosLoading(false);
        }
      }
    }

    loadScenarios();
    return () => {
      ignore = true;
      inFlightScenarioRequests.delete(requestKey);
    };
  }, [
    templateKey,
    selectedPilotId,
    effectiveFuelType,
    variant.locale,
    channel,
    rc,
  ]);

  // Fetch template meta AFTER render completes (for optimization - elements from HTML parsing)
  // This is separate from scenarios fetch to allow scenarios to show even if render fails
  React.useEffect(() => {
    if (!shouldFetchTemplateMeta || !templateKey) {
      return;
    }

    const requestKey = `${templateKey}-${elementKey || ''}`;

    if (inFlightTemplateMetaRequests.has(requestKey)) {
      console.log('[WysiwygPage] Template meta request already in flight, skipping duplicate:', requestKey);
      return;
    }

    inFlightTemplateMetaRequests.add(requestKey);
    lastFetchedTemplateKeyRef.current = templateKey;

    let ignore = false;
    async function loadTemplateMeta() {
      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        console.log('[WysiwygPage] Fetching template meta:', {
          templateKey,
          elementKey,
          requestKey
        });

        const meta = await api.getTemplateMeta(templateKey, elementKey);

        if (ignore) {
          return;
        }

        inFlightTemplateMetaRequests.delete(requestKey);

        if (!availableElements || availableElements.length === 0) {
          setAvailableElements(meta.elements || []);
        }

        setShouldFetchTemplateMeta(false);
      } catch (error: any) {
        if (!ignore) {
          console.error('[WysiwygPage] Failed to load template meta:', error);
          inFlightTemplateMetaRequests.delete(requestKey);
          setShouldFetchTemplateMeta(false);
        }
      }
    }
    loadTemplateMeta();
    return () => {
      ignore = true;
    };
  }, [
    shouldFetchTemplateMeta,
    templateKey,
    elementKey,
    rc,
    availableElements
  ]);

  // Stable callback for preview hover events (throttled at emission side)
  const handlePreviewHover = React.useCallback((payload: PreviewHoverPayload | null) => {
    if (isHoverPinned) return; // ignore live hover updates while pinned
    if (payload) console.log('[WysiwygPage] hover:', payload.dataAttrHints);
    setHoverPayload(payload);
  }, [isHoverPinned]);

  const handlePreviewHoverPin = React.useCallback((payload: PreviewHoverPayload) => {
    setIsHoverPinned(true);
    setHoverPayload(payload);
  }, []);

  const handleHoverUnpin = React.useCallback(() => {
    setIsHoverPinned(false);
    setHoverPayload(null);
  }, []);

  // Clear any stale hover highlight whenever the preview re-renders for a new
  // scenario / locale / hierarchy / preview-mode. The PreviewFrame iframe
  // teardown will also emit null once its srcdoc swaps, but there's a gap
  // while the render API call is in flight — this effect closes that gap.
  React.useEffect(() => {
    setIsHoverPinned(false);
    setHoverPayload(null);
  }, [variant.scenario, variant.locale, variant.hierarchy, variant.clusterId, previewMode]);

  React.useEffect(() => {
    if (!hoverInvestigateEnabled) {
      setIsHoverPinned(false);
      setHoverPayload(null);
    }
  }, [hoverInvestigateEnabled]);

  // Wrapper functions to intercept changes and check for unsaved changes
  const handleTemplateChange = React.useCallback((selection: { templateKey: string; fuelType?: string }) => {
    if (modifiedCount > 0 && selection.templateKey !== templateKey) {
      // Show confirmation dialog
      setUnsavedChangesDialog({
        open: true,
        pendingAction: 'template',
        pendingValue: selection, // Store the full selection object
      });
    } else {
      // No unsaved changes, proceed with change
      setTemplateKey(selection.templateKey);
      setSelectedFuelType(selection.fuelType);
      setElementKey('Full Template'); // Reset elementKey when template changes
    }
  }, [modifiedCount, templateKey]);

  const handleElementChange = React.useCallback((newElementKey: string) => {
    if (modifiedCount > 0 && newElementKey !== elementKey) {
      // Show confirmation dialog
      setUnsavedChangesDialog({
        open: true,
        pendingAction: 'element',
        pendingValue: newElementKey,
      });
    } else {
      // No unsaved changes, proceed with change
      setElementKey(newElementKey);
      setHoverPayload(null); // Clear hover on section change
    }
  }, [modifiedCount, elementKey]);

  const handleVariantChangeWithConfirmation = React.useCallback((newVariant: Variant) => {
    // Check if scenario is changing (which triggers re-rendering)
    const scenarioChanged = newVariant.scenario !== variant.scenario;
    
    // Only show dialog if scenario changes (as it triggers re-rendering)
    // Hierarchy and locale changes might not trigger re-rendering, but scenario definitely does
    if (modifiedCount > 0 && scenarioChanged) {
      // Show confirmation dialog
      setUnsavedChangesDialog({
        open: true,
        pendingAction: 'scenario',
        pendingValue: newVariant,
      });
    } else {
      // No unsaved changes or scenario not changing, proceed with change
      setVariant(newVariant);
    }
  }, [modifiedCount, variant]);

  // Handle confirmation dialog actions
  const handleSaveAndContinue = React.useCallback(async () => {
    const { pendingAction, pendingValue } = unsavedChangesDialog;
    // Save changes first - call the actual save function
    if (inspectorSaveRef.current) {
      try {
        await inspectorSaveRef.current();
        // After save, proceed with the pending change
        if (pendingAction === 'template') {
          const selection = pendingValue as { templateKey: string; fuelType?: string };
          setTemplateKey(selection.templateKey);
          setSelectedFuelType(selection.fuelType);
          setElementKey('Full Template'); // Reset elementKey when template changes
        } else if (pendingAction === 'element') {
          setElementKey(pendingValue as string);
        } else if (pendingAction === 'scenario') {
          setVariant(pendingValue as Variant);
        }
        setUnsavedChangesDialog({ open: false, pendingAction: 'template', pendingValue: '' });
      } catch (error) {
        console.error('[WysiwygPage] Error saving before switch:', error);
        toast.error('Failed to save changes. Please try again.');
      }
    } else {
      // Fallback: if no save function available, just proceed with the change
      console.warn('[WysiwygPage] No save function available, proceeding with change');
      if (pendingAction === 'template') {
        const selection = pendingValue as { templateKey: string; fuelType?: string };
        setTemplateKey(selection.templateKey);
        setSelectedFuelType(selection.fuelType);
        setElementKey('Full Template'); // Reset elementKey when template changes
      } else if (pendingAction === 'element') {
        setElementKey(pendingValue as string);
      } else if (pendingAction === 'scenario') {
        setVariant(pendingValue as Variant);
      }
      setUnsavedChangesDialog({ open: false, pendingAction: 'template', pendingValue: '' });
    }
  }, [unsavedChangesDialog]);

  const handleDiscardAndContinue = React.useCallback(() => {
    const { pendingAction, pendingValue } = unsavedChangesDialog;
    // Discard changes and proceed with the pending change
    if (inspectorCancelRef.current) {
      inspectorCancelRef.current();
    }
    if (pendingAction === 'template') {
      const selection = pendingValue as { templateKey: string; fuelType?: string };
      setTemplateKey(selection.templateKey);
      setSelectedFuelType(selection.fuelType);
      setElementKey('Full Template'); // Reset elementKey when template changes
    } else if (pendingAction === 'element') {
      setElementKey(pendingValue as string);
    } else if (pendingAction === 'scenario') {
      setVariant(pendingValue as Variant);
    }
    setUnsavedChangesDialog({ open: false, pendingAction: 'template', pendingValue: '' });
  }, [unsavedChangesDialog]);

  const handleCancelSwitch = React.useCallback(() => {
    // Cancel the switch, keep current state
    setUnsavedChangesDialog({ open: false, pendingAction: 'template', pendingValue: '' });
  }, []);

  // Auto-collapse TemplatesSidebar when Web or HER channel is selected (for larger preview)
  // For Web: collapse both TemplatesSidebar and InspectorPanel
  // For HER: collapse only TemplatesSidebar (keep InspectorPanel open)
  // Collapse on any Web/HER template/element selection or change, not just the first one
  const prevWebSelectionRef = React.useRef<string>(''); // Track previous Web/HER template/element combination
  React.useEffect(() => {
    // If we're on Web or HER channel and have a template selected
    if ((channel === 'Web' || channel === 'HER') && templateKey) {
      const currentSelection = `${templateKey}-${elementKey}`;
      
      // Collapse sidebars whenever a Web/HER item is selected/changed
      // This includes: first Web/HER item, switching between Web/HER items, or any Web/HER-related change
      if (currentSelection !== prevWebSelectionRef.current) {
        // Collapse template sidebar if it is open
        if (sidebarOpen) {
          setSidebarOpen(false);
        }
        // For Web channel only: also collapse inspector panel
        if (channel === 'Web' && inspectorOpen) {
          setInspectorOpen(false);
        }
        prevWebSelectionRef.current = currentSelection;
      }
    } else if (channel !== 'Web' && channel !== 'HER') {
      // Reset tracking when not on Web or HER channel
      prevWebSelectionRef.current = '';
    }
  }, [channel, templateKey, elementKey]); // Track templateKey and elementKey to collapse on any Web/HER item change

  // Prepare available templates list from Redux Level 1 data
  const availableTemplates = React.useMemo(() => {
    if (!level1Data) return [];
    
    // Filter only ACCESS resolutions and extract template info
    return level1Data
      .filter(r => r.resolution === 'ACCESS')
      .map(r => {
        // Extract display name from featureId
        const parts = r.featureId.split('.');
        const lastPart = parts[parts.length - 1] || r.featureId;
        const displayName = lastPart
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        
        return {
          templateKey: r.featureId,
          name: displayName
        };
      });
  }, [level1Data]);

  // Memoize renderReq to prevent unnecessary re-renders in PreviewArea
  // Include refreshKey to force re-render after save while preserving elementKey and scenario
  // Pass templateKey and fuelType as separate properties
  // Gate rendering: don't build a render request while feature metrics are
  // still loading for the new pilot — this prevents blank widgets caused by
  // stale or incomplete data being passed to PreviewArea.
  const renderReq: RenderRequest | null = React.useMemo(() => {
    if (featureMetricsLoading) return null;
    return variantA
      ? { templateKey, fuelType: selectedFuelType, elementKey, variant: { hierarchy: variantA.hierarchy, locale: variantA.locale, scenario: variantA.scenario, clusterId: variantA.clusterId }, previewMode: 'desktop', values, refreshKey }
      : null;
  }, [featureMetricsLoading, templateKey, selectedFuelType, variantA?.hierarchy, variantA?.locale, variantA?.scenario, variantA?.clusterId, elementKey, values, refreshKey]);

  const renderReqB: RenderRequest | null = React.useMemo(() => {
    return variantB
      ? { templateKey: variantB.templateKey, fuelType: variantB.fuelType, elementKey, variant: { hierarchy: variantB.hierarchy, locale: variantB.locale, scenario: variantB.scenario, clusterId: variantB.clusterId }, previewMode: 'desktop', values }
      : null;
  }, [variantB?.templateKey, variantB?.fuelType, variantB?.hierarchy, variantB?.locale, variantB?.scenario, variantB?.clusterId, elementKey, values]);

  const handleVariantChange = (variant: 'A' | 'B', data: VariantWithTemplate) => {
    if (variant === 'A') {
      // Update the main templateKey and variant - variantA will be recalculated via useMemo
      if (data.templateKey !== templateKey) {
        setTemplateKey(data.templateKey);
        setElementKey('Full Template'); // Reset elementKey when template changes
      }
      setVariant({ hierarchy: data.hierarchy, locale: data.locale, scenario: data.scenario, clusterId: data.clusterId });
    } else {
      setVariantB(data);
    }
  };

  const handleCompareRun = () => {
    // Both variants are already set via onChange, just close the dialog
    // The renderReq and renderReqB will automatically update
  };

  return (
    <WidgetErrorBoundary>
      <WidgetSdkErrorBoundary>
        <WidgetSdkProvider>
        <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 8rem)',
          gap: 0,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            width: sidebarOpen ? 300 : 48,
            minWidth: sidebarOpen ? 300 : 48,
            transition: 'width 300ms ease-in-out',
            flexShrink: 0,
            overflow: 'hidden'
          }}
        >
          <TemplatesSidebar
            onSelect={handleTemplateChange}
            selectedTemplate={{ templateKey, fuelType: selectedFuelType }}
            isOpen={sidebarOpen}
            onToggleOpen={() => setSidebarOpen(o => !o)}
            disabled={previewBusy}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            pl: 2,
            gap: 2,
            overflow: 'hidden'
          }}
        >
          {/* EditorToolbar Upper Section - Full Width */}
          <Box sx={{ width: '100%' }}>
            <EditorToolbar
              templateKey={templateKey}
              fuelType={selectedFuelType}
              variant={variant}
              channel={channel}
              previewMode={previewMode}
              onVariantChange={handleVariantChangeWithConfirmation}
              elementKey={elementKey}
              onElementChange={handleElementChange}
              disabled={previewBusy}
              onPreviewModeChange={setPreviewMode}
              hoverInvestigateEnabled={hoverInvestigateEnabled}
              onHoverInvestigateChange={setHoverInvestigateEnabled}
              onOpenCompare={() => setCompareOpen(true)}
              onCancel={() => {
                // Call InspectorPanel cancel function if available
                if (inspectorCancelRef.current) {
                  inspectorCancelRef.current();
                }
              }}
              onSave={async () => {
                // Call InspectorPanel save function if available
                if (inspectorSaveRef.current) {
                  await inspectorSaveRef.current();
                } else {
                  // Fallback to old save if InspectorPanel save not available
                  if (!templateKey) return;
                  try {
                    const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);

                    // Resolve "default" scenario to actual scenario ID from data scenarios API
                    const resolvedScenario = await resolveScenarioId(
                      variant.scenario || 'default',
                      templateKey, // Clean template key
                      channel || 'Email',
                      selectedPilotId || '',
                      fuelType,
                      variant.locale || 'en_US',
                      (pilot_id, fuel_type, locale) => api.getDataScenarios(pilot_id, fuel_type, locale)
                    );

                    const resp = await api.saveChanges({
                      templateKey, // Clean template key
                      elementKey,
                      variant: { ...variant, scenario: resolvedScenario },
                      changes: { text: [], colors: [], images: [], metadata: [] },
                      message: 'Quick save'
                    });
                    toast.success(`Saved (version ${resp.versionId})`);
                  } catch (e: any) {
                    toast.error('Save failed');
                  }
                }
              }}
              renderOnlyUpperSection={true}
              availableElements={availableElements}
              onRenderComplete={handleRenderComplete}
              modifiedCount={modifiedCount}
              exportInProgressAction={exportInProgressAction}
            />
          </Box>

          {/* Flex Row: Left side (Lower Section + PreviewArea stacked), Right side (InspectorPanel) */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            {/* Left side: Lower Section and PreviewArea stacked vertically */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                minHeight: 0,
                gap: 2,
                overflow: 'hidden'
              }}
            >
              {/* EditorToolbar Lower Section */}
              <Box sx={{ flexShrink: 0 }}>
                <EditorToolbar
                  templateKey={templateKey}
                  fuelType={selectedFuelType}
                  variant={variant}
                  channel={channel}
                  previewMode={previewMode}
                  onVariantChange={handleVariantChangeWithConfirmation}
                  elementKey={elementKey}
                  onElementChange={handleElementChange}
                  onPreviewModeChange={setPreviewMode}
                  hoverInvestigateEnabled={hoverInvestigateEnabled}
                  onHoverInvestigateChange={setHoverInvestigateEnabled}
                  onOpenCompare={() => setCompareOpen(true)}
                  onCancel={() => {
                    // Call InspectorPanel cancel function if available
                    if (inspectorCancelRef.current) {
                      inspectorCancelRef.current();
                    }
                  }}
                  onSave={async () => {
                    // Call InspectorPanel save function if available
                    if (inspectorSaveRef.current) {
                      await inspectorSaveRef.current();
                    } else {
                      // Fallback to old save if InspectorPanel save not available
                      if (!templateKey) return;
                      try {
                        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
                        const resp = await api.saveChanges({ templateKey, elementKey, variant, changes: { text: [], colors: [], images: [], metadata: [] }, message: 'Quick save' });
                        toast.success(`Saved (version ${resp.versionId})`);
                      } catch (e: any) {
                        toast.error('Save failed');
                      }
                    }
                  }}
                  renderOnlyLowerSection={true}
                  availableElements={availableElements}
                  onRenderComplete={handleRenderComplete}
                  modifiedCount={modifiedCount}
                  canExport={canExport}
                  onExport={handleExport}
                  exportDisabledReason={exportDisabledReason}
                  exportInProgressAction={exportInProgressAction}
                />
              </Box>

              {/* PreviewArea */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                  minHeight: 0,
                  overflow: 'hidden'
                }}
              >
                <PreviewArea
                  ref={previewAreaRef}
                  request={renderReq}
                  rightRequest={renderReqB}
                  previewMode={previewMode}
                  channel={channel}
                  desktopPreviewMaxWidth={inspectorOpen ? 672 : 1024}
                  isFullTemplateSelected={elementKey === 'Full Template'}
                  elementKey={elementKey}
                  variant={variant}
                  onLoadingChange={setPreviewBusy}
                  onPreviewHover={handlePreviewHover}
                  onPreviewHoverPin={handlePreviewHoverPin}
                  hoverInvestigateEnabled={hoverInvestigateEnabled}
                  showColorTooltip={hoverInvestigateEnabled && inspectorActiveTab === 'colors'}
                  onSectionsParsed={(sections) => {
                    // Update available elements from parsed HTML sections
                    setAvailableElements(sections);
                    // If current elementKey is not in the new list, reset to Full Template
                    if (!sections.find(s => s.elementKey === elementKey)) {
                      setElementKey('Full Template');
                    }
                  }}
                  onRenderComplete={handleRenderComplete}
                  onHtmlChange={(html) => {
                    setRenderedHtml(html);
                  }}
                  smsTextFields={smsTextFields}
                />
              </Box>
            </Box>

            {/* Right side: InspectorPanel */}
            <Box
              sx={{
                width: inspectorOpen ? 400 : 48,
                minWidth: inspectorOpen ? 400 : 48,
                transition: 'width 300ms ease-in-out',
                flexShrink: 0,
                overflow: 'hidden'
              }}
            >
              <InspectorPanel
                templateKey={templateKey}
                fuelType={selectedFuelType}
                elementKey={elementKey}
                variant={variant}
                onValuesChange={setValues}
                isOpen={inspectorOpen}
                onToggleOpen={() => setInspectorOpen(o => !o)}
                renderedHtml={renderedHtml}
                refreshKey={refreshKey}
                hoverPayload={hoverPayload}
                isHoverPinned={isHoverPinned}
                onHoverUnpin={handleHoverUnpin}
                onActiveTabChange={setInspectorActiveTab}
                onSaveReady={(saveFn) => {
                  inspectorSaveRef.current = saveFn;
                }}
                onCancelReady={(cancelFn) => {
                  inspectorCancelRef.current = cancelFn;
                }}
                onModifiedCountChange={(count) => {
                  setModifiedCount(count);
                }}
                onSaveSuccess={() => {
                  // Increment refreshKey to trigger preview re-render after save
                  // This preserves the current elementKey and scenario
                  setRefreshKey(prev => prev + 1);
                }}
                onTextFieldsChange={setSmsTextFields}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <CompareDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        variantA={variantA}
        variantB={variantB}
        onChange={handleVariantChange}
        onRun={handleCompareRun}
        availableTemplates={availableTemplates}
        availableScenarios={availableScenarios}
        availableClusters={[]}
      />

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog 
        open={unsavedChangesDialog.open} 
        onClose={handleCancelSwitch}
        aria-labelledby="unsaved-changes-dialog-title"
        aria-describedby="unsaved-changes-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="unsaved-changes-dialog-title">
          Unsaved Changes
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="unsaved-changes-dialog-description">
            You have {modifiedCount} unsaved {modifiedCount === 1 ? 'change' : 'changes'}. 
            What would you like to do before switching?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSwitch}>
            Cancel
          </Button>
          <Button onClick={handleDiscardAndContinue} color="error" variant="outlined">
            Discard Changes
          </Button>
          <Button onClick={handleSaveAndContinue} color="primary" variant="contained" autoFocus>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
        </WidgetSdkProvider>
      </WidgetSdkErrorBoundary>
    </WidgetErrorBoundary>
  );
}

export default WysiwygPage;
